import React, { useState, useEffect } from "react";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { MessageSquare, Clock, Eye, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import {
  collection,
  query,
  where,
  getDocs,
  getFirestore,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { initializeApp } from "firebase/app";
import { firebaseConfig, isDemoMode } from "@/config/firebase";
import { updatePracticalLessonProgress } from "@/utils/firestoreService";

let db;
try {
  const app = initializeApp(firebaseConfig);
  db = getFirestore(app);
} catch (error) {
  console.error("Firebase initialization error:", error);
}

const EnrolledStudents = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuth();
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEnrollment, setSelectedEnrollment] = useState(null);
  const [progressDialogOpen, setProgressDialogOpen] = useState(false);
  const [hoursToAdd, setHoursToAdd] = useState(1);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedStudentDetails, setSelectedStudentDetails] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filteredEnrollments, setFilteredEnrollments] = useState([]);

  useEffect(() => {
    const fetchEnrollments = async () => {
      if (!user?.id) {
        console.log("No user ID found");
        setLoading(false);
        return;
      }
      
      setLoading(true);
      try {
        console.log("Fetching enrollments for instructor with ID:", user.id);
        
        if (isDemoMode || !db) {
          console.warn("Demo mode or Firestore not initialized - using mock data");
          const mockEnrollments = [
            {
              id: "1",
              studentName: "Demo Student",
              studentEmail: "demo.student@example.com",
              courseName: "Basic Driving Course",
              startDate: "2025-04-25",
              preferredTime: "10:00 AM",
              status: "assigned",
              userId: "demo-user-id",
              totalHours: 20,
              hoursTaken: 5,
              hoursRemaining: 15,
              totalAmount: 1200,
              paymentStatus: "partially_paid",
              paymentToInstructor: "unpaid"
            }
          ];
          setEnrollments(mockEnrollments);
          setLoading(false);
          return;
        }
        
        const enrollmentsRef = collection(db, "enrollments");
        const q = query(enrollmentsRef, where("instructorId", "==", user.id));
        
        console.log("Executing Firestore query for instructor:", user.id);
        const querySnapshot = await getDocs(q);
        
        const fetchedEnrollments = [];
        querySnapshot.forEach((doc) => {
          fetchedEnrollments.push({
            id: doc.id,
            ...doc.data()
          });
        });
        
        console.log("Fetched enrollments from Firestore:", fetchedEnrollments);
        setEnrollments(fetchedEnrollments);
      } catch (error) {
        console.error("Error fetching enrollments:", error);
        toast({
          title: "Error",
          description: "Failed to load enrolled students. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };
    
    fetchEnrollments();
  }, [user, toast]);

  // Add search filter effect
  useEffect(() => {
    if (!searchQuery.trim()) {
      setFilteredEnrollments(enrollments);
      return;
    }

    const query = searchQuery.toLowerCase();
    const filtered = enrollments.filter(enrollment => 
      enrollment.studentName?.toLowerCase().includes(query) ||
      enrollment.studentEmail?.toLowerCase().includes(query) ||
      enrollment.courseName?.toLowerCase().includes(query) ||
      enrollment.status?.toLowerCase().includes(query)
    );
    setFilteredEnrollments(filtered);
  }, [searchQuery, enrollments]);

  const handleMessageStudent = (userId, studentName, studentEmail) => {
    console.log("Message student clicked for userId:", userId);
    
    if (!userId) {
      console.error("Student userId is missing");
      toast({
        title: "Error",
        description: "Cannot message student - missing user ID.",
        variant: "destructive",
      });
      return;
    }
    
    // Navigate to the messages page with the student information
    navigate("/instructor-dashboard/messages", { 
      state: { 
        recipientId: userId,
        recipientInfo: {
          id: userId,
          name: studentName,
          email: studentEmail,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(studentName)}&background=random`,
        }
      } 
    });
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString();
    } catch (error) {
      return "Invalid date";
    }
  };

  const getProgressPercentage = (enrollment) => {
    if (!enrollment.totalHours) {
      return 0;
    }
    
    const hoursTaken = enrollment.hoursTaken || 0;
    return Math.min(100, Math.round((hoursTaken / enrollment.totalHours) * 100));
  };

  const openProgressDialog = (enrollment) => {
    setSelectedEnrollment(enrollment);
    setHoursToAdd(1); // Default to 1 hour
    setProgressDialogOpen(true);
  };

  const handleUpdateProgress = async () => {
    if (!selectedEnrollment || !selectedEnrollment.id) return;
    
    try {
      console.log("Updating progress for enrollment:", selectedEnrollment.id, "Hours to add:", hoursToAdd);
      
      // Use the firestoreService to update the progress
      const updatedEnrollment = await updatePracticalLessonProgress(
        selectedEnrollment.id, 
        hoursToAdd
      );
      
      if (updatedEnrollment) {
        console.log("Progress updated successfully:", updatedEnrollment);
        
        // Update the local state with the new values from the database
        setEnrollments(enrollments.map(e => 
          e.id === selectedEnrollment.id ? {
            ...e,
            hoursTaken: updatedEnrollment.hoursTaken,
            hoursRemaining: updatedEnrollment.hoursRemaining
          } : e
        ));
        
        toast({
          title: "Progress Updated",
          description: `Added ${hoursToAdd} hours to ${selectedEnrollment.studentName}'s progress`,
        });
      } else {
        // Fallback for demo mode or if firestoreService doesn't return updated enrollment
        const currentHoursTaken = selectedEnrollment.hoursTaken || 0;
        const totalHours = selectedEnrollment.totalHours || 0;
        
        const newHoursTaken = currentHoursTaken + hoursToAdd;
        const newHoursRemaining = Math.max(0, totalHours - newHoursTaken);
        
        // Update local state
        setEnrollments(enrollments.map(e => 
          e.id === selectedEnrollment.id ? {
            ...e,
            hoursTaken: newHoursTaken,
            hoursRemaining: newHoursRemaining
          } : e
        ));
        
        // If in demo mode and db is available, update localStorage or Firestore directly
        if (isDemoMode) {
          const enrollmentsInStorage = JSON.parse(localStorage.getItem("enrollments") || "[]");
          const updatedEnrollmentsInStorage = enrollmentsInStorage.map(e => 
            e.id === selectedEnrollment.id ? {
              ...e,
              hoursTaken: newHoursTaken,
              hoursRemaining: newHoursRemaining,
              updatedAt: new Date().toISOString()
            } : e
          );
          localStorage.setItem("enrollments", JSON.stringify(updatedEnrollmentsInStorage));
        } else if (db) {
          // Directly update Firestore if db is available
          const enrollmentRef = doc(db, "enrollments", selectedEnrollment.id);
          await updateDoc(enrollmentRef, {
            hoursTaken: newHoursTaken,
            hoursRemaining: newHoursRemaining,
            updatedAt: new Date().toISOString()
          });
        }
        
        toast({
          title: "Progress Updated",
          description: `Added ${hoursToAdd} hours to ${selectedEnrollment.studentName}'s progress`,
        });
      }
      
      setProgressDialogOpen(false);
    } catch (error) {
      console.error("Error updating progress:", error);
      toast({
        title: "Update Failed",
        description: "Could not update student progress",
        variant: "destructive"
      });
    }
  };

  const openDetailsDialog = (enrollment) => {
    setSelectedStudentDetails(enrollment);
    setDetailsDialogOpen(true);
  };

  return (
    <DashboardLayout role="instructor">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold">Enrolled Students</h1>
          <p className="text-gray-500 mt-2">View and manage your enrolled students</p>
        </div>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by student name, email, course, or status..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>

        {loading ? (
          <div className="flex justify-center items-center h-64">
            <p className="text-gray-500">Loading enrolled students...</p>
          </div>
        ) : filteredEnrollments.length === 0 ? (
          <div className="flex flex-col justify-center items-center h-64 bg-gray-50 rounded-lg border p-6">
            <p className="text-gray-700 font-medium text-lg">
              {searchQuery ? "No matching students found" : "No students enrolled yet."}
            </p>
            <p className="text-gray-500 text-center mt-2">
              {searchQuery 
                ? "Try adjusting your search terms"
                : "When students are assigned to you, they will appear here."}
            </p>
          </div>
        ) : (
          <>
            <div className="grid gap-6 md:hidden">
              {filteredEnrollments.map((enrollment) => (
                <Card key={enrollment.id} className="overflow-hidden">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-4">
                      <Avatar className="h-12 w-12">
                        <img 
                          src={`https://ui-avatars.com/api/?name=${encodeURIComponent(enrollment.studentName)}&background=random`}
                          alt={enrollment.studentName} 
                        />
                      </Avatar>
                      <div>
                        <h3 className="font-semibold">{enrollment.studentName}</h3>
                        <p className="text-sm text-gray-500">{enrollment.studentEmail}</p>
                      </div>
                    </div>

                    <div className="mt-4 space-y-4">
                      <div>
                        <p className="text-sm font-medium">Course</p>
                        <p className="mt-1">{enrollment.courseName}</p>
                      </div>

                      <div>
                        <p className="text-sm font-medium">Start Date</p>
                        <p className="mt-1">{formatDate(enrollment.startDate)}</p>
                      </div>

                      <div>
                        <p className="text-sm font-medium">Preferred Time</p>
                        <p className="mt-1">{enrollment.preferredTime}</p>
                      </div>

                      {enrollment.totalHours && (
                        <div>
                          <p className="text-sm font-medium">Progress</p>
                          <div className="mt-2 space-y-1">
                            <Progress value={getProgressPercentage(enrollment)} className="h-2" />
                            <p className="text-xs text-gray-500">
                              {enrollment.hoursTaken || 0} of {enrollment.totalHours} hours completed
                            </p>
                          </div>
                        </div>
                      )}

                      <div>
                        <p className="text-sm font-medium">Payment</p>
                        <p className="mt-1">
                          Student paid: £{enrollment.totalAmount || 0}
                          <Badge className="ml-2" variant="outline">
                            {enrollment.paymentToInstructor === 'paid' ? 'Paid to you' : 'Pending payment to you'}
                          </Badge>
                        </p>
                      </div>

                      <div className="flex items-center justify-between">
                        <Badge variant={enrollment.status === "assigned" ? "default" : "secondary"}>
                          {enrollment.status}
                        </Badge>
                        <div className="space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openDetailsDialog(enrollment)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleMessageStudent(
                              enrollment.userId,
                              enrollment.studentName,
                              enrollment.studentEmail
                            )}
                          >
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Message
                          </Button>
                          {enrollment.totalHours && (
                            <Button
                              size="sm"
                              onClick={() => openProgressDialog(enrollment)}
                            >
                              <Clock className="h-4 w-4 mr-1" />
                              Update Hours
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="hidden md:block">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Student</TableHead>
                    <TableHead>Course</TableHead>
                    <TableHead>Progress</TableHead>
                    <TableHead>Start Date</TableHead>
                    <TableHead>Payment Status</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredEnrollments.map((enrollment) => (
                    <TableRow key={enrollment.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8">
                            <img 
                              src={`https://ui-avatars.com/api/?name=${encodeURIComponent(enrollment.studentName)}&background=random`}
                              alt={enrollment.studentName}
                            />
                          </Avatar>
                          <div>
                            <p className="font-medium">{enrollment.studentName}</p>
                            <p className="text-xs text-gray-500">{enrollment.studentEmail}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{enrollment.courseName}</TableCell>
                      <TableCell>
                        {enrollment.totalHours ? (
                          <div className="space-y-1 w-32">
                            <Progress value={getProgressPercentage(enrollment)} className="h-2" />
                            <div className="text-xs text-gray-500">
                              {enrollment.hoursTaken || 0} of {enrollment.totalHours} hrs completed
                            </div>
                          </div>
                        ) : (
                          "N/A"
                        )}
                      </TableCell>
                      <TableCell>{formatDate(enrollment.startDate)}</TableCell>
                      <TableCell>
                        <div>
                          <p>£{enrollment.totalAmount || 0}</p>
                          <Badge className="mt-1" variant="outline">
                            {enrollment.paymentToInstructor === 'paid' ? 'Paid to you' : 'Pending'}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant={enrollment.status === "assigned" ? "default" : "secondary"}>
                          {enrollment.status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => openDetailsDialog(enrollment)}
                          >
                            <Eye className="h-4 w-4 mr-2" />
                            View Details
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleMessageStudent(
                              enrollment.userId,
                              enrollment.studentName,
                              enrollment.studentEmail
                            )}
                          >
                            <MessageSquare className="h-4 w-4 mr-2" />
                            Message
                          </Button>
                          {enrollment.totalHours && (
                            <Button
                              size="sm"
                              onClick={() => openProgressDialog(enrollment)}
                            >
                              <Clock className="h-4 w-4 mr-1" />
                              Update Hours
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </>
        )}
      </div>

      {/* Student Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Student Details</DialogTitle>
            <DialogDescription>
              Detailed information about {selectedStudentDetails?.studentName}'s enrollment
            </DialogDescription>
          </DialogHeader>

          {selectedStudentDetails && (
            <div className="space-y-6 py-4">
              {/* Student Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Student Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Name</p>
                    <p className="font-medium break-words">{selectedStudentDetails.studentName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Email</p>
                    <p className="font-medium break-words">{selectedStudentDetails.studentEmail}</p>
                  </div>
                </div>
              </div>

              {/* Course Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Course Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Course Name</p>
                    <p className="font-medium break-words">{selectedStudentDetails.courseName}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Course Type</p>
                    <p className="font-medium break-words">{selectedStudentDetails.courseType || 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Start Date</p>
                    <p className="font-medium">{selectedStudentDetails.startDate ? new Date(selectedStudentDetails.startDate).toLocaleDateString() : 'N/A'}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Preferred Time</p>
                    <p className="font-medium break-words">{selectedStudentDetails.preferredTime || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {/* Progress Information */}
              {selectedStudentDetails.totalHours && (
                <div className="space-y-4">
                  <h3 className="font-semibold text-lg">Progress Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-500">Total Hours</p>
                      <p className="font-medium">{selectedStudentDetails.totalHours} hours</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Hours Taken</p>
                      <p className="font-medium">{selectedStudentDetails.hoursTaken || 0} hours</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Hours Remaining</p>
                      <p className="font-medium">
                        {selectedStudentDetails.hoursRemaining || 
                          (selectedStudentDetails.totalHours - (selectedStudentDetails.hoursTaken || 0))} hours
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Progress</p>
                      <div className="mt-1">
                        <Progress value={getProgressPercentage(selectedStudentDetails)} className="h-2" />
                        <p className="text-xs text-gray-500 mt-1">
                          {getProgressPercentage(selectedStudentDetails)}% Complete
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Payment Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Payment Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Total Amount</p>
                    <p className="font-medium">£{selectedStudentDetails.totalAmount || 0}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payment Status</p>
                    <Badge variant="outline" className={selectedStudentDetails.paymentToInstructor === 'paid' ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}>
                      {selectedStudentDetails.paymentToInstructor === 'paid' ? 'Paid' : 'Unpaid'}
                    </Badge>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Additional Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Enrollment Status</p>
                    <Badge
                      className={
                        selectedStudentDetails.status === "completed" ? "bg-green-500" :
                        selectedStudentDetails.status === "assigned" ? "bg-blue-500" : 
                        "bg-yellow-500"
                      }
                    >
                      {selectedStudentDetails.status}
                    </Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Enrollment Date</p>
                    <p className="font-medium">
                      {selectedStudentDetails.createdAt ? new Date(selectedStudentDetails.createdAt).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <DialogFooter className="sticky bottom-0 bg-background pt-4 border-t">
            <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Existing Progress Dialog */}
      <Dialog open={progressDialogOpen} onOpenChange={setProgressDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Student Progress</DialogTitle>
            <DialogDescription>
              Record hours taken by {selectedEnrollment?.studentName} for {selectedEnrollment?.courseName}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Current Progress</Label>
              <div className="text-sm">
                <p><strong>Hours Taken:</strong> {selectedEnrollment?.hoursTaken || 0} hrs</p>
                <p><strong>Hours Remaining:</strong> {selectedEnrollment?.hoursRemaining || 
                  (selectedEnrollment?.totalHours - (selectedEnrollment?.hoursTaken || 0)) || 0} hrs</p>
                <p><strong>Total Course Hours:</strong> {selectedEnrollment?.totalHours || 0} hrs</p>
              </div>
              
              {selectedEnrollment && (
                <div className="mt-2">
                  <Progress value={getProgressPercentage(selectedEnrollment)} className="h-2" />
                </div>
              )}
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="hours-to-add">Hours to Add</Label>
              <Input
                id="hours-to-add"
                type="number"
                min={0.5}
                step={0.5}
                max={selectedEnrollment?.hoursRemaining || 
                  (selectedEnrollment?.totalHours - (selectedEnrollment?.hoursTaken || 0)) || 0}
                value={hoursToAdd}
                onChange={(e) => setHoursToAdd(parseFloat(e.target.value) || 0)}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setProgressDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleUpdateProgress}
            >
              Update Progress
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
};

export default EnrolledStudents;
